import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
} from 'react';
import classNames from 'classnames/bind';
import Field from 'components/Field/Field';
import {
  SelectableFormPanelFinalFormAdapter,
  SIZES,
} from 'components/SelectableFormPanel/SelectableFormPanel';
import styles from './AnimatedMultiSelectable.module.css';

const cx = classNames.bind(styles);

const TYPES = {
  DOUBLES: 'doubles',
  FULL_WIDTH: 'full-width',
} as const;

type TypeValue = (typeof TYPES)[keyof typeof TYPES];

interface Option {
  key?: string;
  name: string;
  title?: string;
  needsComment?: boolean;
  filesRequired?: boolean;
  fileLabel?: string;
}

interface AnimatedMultiSelectableProps {
  name: string;
  form: unknown;
  meta: {
    touched?: boolean;
    error?: Record<string, unknown>;
  };
  options: Option[];
  newFieldComponent?: boolean;
  ignoreError?: boolean;
  type?: TypeValue;
  value?: unknown;
  onChange?: (value: unknown) => void;
  [key: string]: unknown;
}

const AnimatedMultiSelectable: React.FC<AnimatedMultiSelectableProps> = ({
  name,
  form,
  meta,
  options,
  newFieldComponent = false,
  ignoreError = false,
  type = TYPES.FULL_WIDTH,
  ...rest
}) => {
  const [newlyAddedOptions, setNewlyAddedOptions] = useState<Set<string>>(
    new Set(),
  );
  const [optionsToRender, setOptionsToRender] = useState<Option[]>(options);

  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const animationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const previousOptionsRef = useRef<Option[]>(options);
  const previousOptionKeysRef = useRef<Set<string>>(
    new Set(options.map((option) => option.key || option.name)),
  );
  const shouldAnimateRef = useRef<boolean>(false);
  const positionSnapshotRef = useRef<
    Map<string, { top: number; left: number; height: number }>
  >(new Map());

  const clearAllAnimations = useCallback(() => {
    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current);
      animationTimeoutRef.current = null;
    }
    setNewlyAddedOptions(new Set());
  }, []);

  const capturePositions = useCallback(() => {
    const snapshot = new Map();
    if (containerRef.current) {
      itemRefs.current.forEach((ref, key) => {
        if (ref && ref.getBoundingClientRect) {
          const rect = ref.getBoundingClientRect();
          snapshot.set(key, {
            top: rect.top,
            left: rect.left,
            height: rect.height,
          });
        }
      });
    }
    return snapshot;
  }, []);

  const animatePositionChanges = useCallback(
    (
      snapshot: Map<string, { top: number; left: number; height: number }>,
      prevOptions: Option[],
      currentOptions: Option[],
    ) => {
      if (!containerRef.current) return;

      requestAnimationFrame(() => {
        prevOptions.forEach((option) => {
          const key = option.key || option.name;
          const ref = itemRefs.current.get(key);
          const oldPosition = snapshot.get(key);

          if (
            ref &&
            oldPosition &&
            currentOptions.find((o) => (o.key || o.name) === key)
          ) {
            const newPosition = ref.getBoundingClientRect();
            const deltaY = oldPosition.top - newPosition.top;

            if (Math.abs(deltaY) > 1) {
              // Apply initial transform to put item back to old position
              ref.style.transform = `translateY(${deltaY}px)`;
              ref.style.transition = 'none';

              // Force a reflow
              void ref.offsetHeight;

              // Animate to new position (content sliding down)
              ref.style.transition =
                'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
              ref.style.transform = 'translateY(0)';

              // Clean up after animation
              setTimeout(() => {
                if (ref && ref.style) {
                  ref.style.transform = '';
                  ref.style.transition = '';
                }
              }, 300);
            }
          }
        });

        // Clear the snapshot AFTER animation has started
        positionSnapshotRef.current.clear();
        shouldAnimateRef.current = false;
      });
    },
    [],
  );

  // Handle options changes and capture positions BEFORE DOM updates
  useEffect(() => {
    const currentOptionKeys = new Set(
      options.map((option) => option.key || option.name),
    );
    const newlyAddedKeys = new Set(
      [...currentOptionKeys].filter(
        (key) => !previousOptionKeysRef.current.has(key),
      ),
    );
    const removedKeys = new Set(
      [...previousOptionKeysRef.current].filter(
        (key) => !currentOptionKeys.has(key),
      ),
    );

    // Check if we need to animate additions or removals
    const shouldAnimateAdditions =
      newlyAddedKeys.size > 0 && previousOptionKeysRef.current.size > 0;
    const shouldAnimateRemovals =
      removedKeys.size > 0 && previousOptionKeysRef.current.size > 0;

    if (shouldAnimateAdditions || shouldAnimateRemovals) {
      // Capture positions BEFORE DOM changes
      positionSnapshotRef.current = capturePositions();
      shouldAnimateRef.current = true;
      clearAllAnimations();
    }

    // Store current state BEFORE updating
    previousOptionsRef.current = optionsToRender;

    // Always update state and refs
    setOptionsToRender(options);
    previousOptionKeysRef.current = currentOptionKeys;

    // Set up new options animation (slide in from right)
    if (newlyAddedKeys.size > 0 && previousOptionKeysRef.current.size > 0) {
      setNewlyAddedOptions((prev) => new Set([...prev, ...newlyAddedKeys]));

      // Clear animations after they complete
      animationTimeoutRef.current = setTimeout(() => {
        setNewlyAddedOptions(
          (prev) =>
            new Set([...prev].filter((key) => !newlyAddedKeys.has(key))),
        );
      }, 1200);
    }
  }, [options]);

  // Animate after DOM updates (equivalent to componentDidUpdate with snapshot)
  useLayoutEffect(() => {
    if (shouldAnimateRef.current && positionSnapshotRef.current.size > 0) {
      // Animate position changes for existing items (content sliding down or up)
      animatePositionChanges(
        positionSnapshotRef.current,
        previousOptionsRef.current,
        options,
      );
    }
  });

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearAllAnimations();
    };
  }, [clearAllAnimations]);

  const controlledProps: { error?: Record<string, unknown> } = {};

  if (!ignoreError) {
    if (newFieldComponent) {
      controlledProps.error = meta.error;
    } else {
      controlledProps.error = meta.touched ? meta.error : {};
    }
  }

  return (
    <div className={cx('container')} ref={containerRef}>
      {optionsToRender.map((option) => {
        const optionKey = option.key || option.name;
        const isNewlyAdded = newlyAddedOptions.has(optionKey);

        return (
          <div
            className={cx('item', type, {
              'slide-in': isNewlyAdded,
            })}
            key={optionKey}
            ref={(ref) => {
              if (ref) {
                itemRefs.current.set(optionKey, ref);
              } else {
                itemRefs.current.delete(optionKey);
              }
            }}
          >
            <Field
              nestedField
              component={SelectableFormPanelFinalFormAdapter}
              size={SIZES.SMALL}
              showCheckmark
              {...rest}
              {...option}
              name={`${name}.${option.name}`}
              form={form as any}
            />
          </div>
        );
      })}
    </div>
  );
};

interface AnimatedMultiSelectableFinalFormAdapterProps {
  input: Record<string, unknown>;
  options: Option[];
  [key: string]: unknown;
}

export const AnimatedMultiSelectableFinalFormAdapter: React.FC<
  AnimatedMultiSelectableFinalFormAdapterProps
> = ({ input, options, ...rest }) => (
  <AnimatedMultiSelectable
    {...(input as AnimatedMultiSelectableProps)}
    options={options}
    {...rest}
  />
);
